import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE, api } from './api'
import { call } from './errors'

export type InspectionCallReceipt = {
  provider: string
  requested_model: string
  actual_model: string
  call_id?: string
  prompt_tokens: number
  output_tokens: number
}

export type InspectionFinding = {
  id: string
  job_id: string
  account_id: string
  project_id: string
  task_id: string
  release_id?: string
  severity: 'blocker' | 'major' | 'minor'
  rule: string
  summary: string
  evidence: string
  open: boolean
  created_at: string
  base_sha?: string
  candidate_sha?: string
  changed_paths?: string[]
  call_receipt?: InspectionCallReceipt
}

export type InspectionFindingsList = {
  items: InspectionFinding[]
  last_completed_at?: string | null
}

export type InspectionJob = {
  id: string
  account_id: string
  project_id: string
  task_id?: string
  release_id: string
  phase: string
  deployment_sha: string
  created_at: string
  completed_at?: string
  evidence: {
    base_sha?: string
    candidate_sha?: string
    changed_paths?: string[]
    changed_paths_available?: boolean
  }
  call_receipt?: InspectionCallReceipt
}

export type InspectionFindingsFilter = {
  releaseId?: string
  jobId?: string
}

export function inspectionFindingsQueryKey(filter: InspectionFindingsFilter = {}) {
  return ['inspection-findings', filter.releaseId ?? '', filter.jobId ?? ''] as const
}

function findingsURL(filter: InspectionFindingsFilter = {}): string {
  const params = new URLSearchParams()
  if (filter.releaseId) params.set('release_id', filter.releaseId)
  if (filter.jobId) params.set('job_id', filter.jobId)
  const query = params.toString()
  return query ? `${API_BASE}/inspection-findings?${query}` : `${API_BASE}/inspection-findings`
}

async function readJSON<T>(path: string, url: string): Promise<T> {
  return call(path, async () => {
    const response = await fetch(url, { credentials: 'include' })
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) {
      return { response }
    }
    const body = await response.json()
    if (!response.ok) {
      return { error: body, response }
    }
    return { data: body as T, response }
  })
}

export function listInspectionFindings(
  filter: InspectionFindingsFilter = {},
): Promise<InspectionFindingsList> {
  return readJSON('/inspection-findings', findingsURL(filter))
}

export function getInspectionFinding(id: string): Promise<InspectionFinding> {
  return readJSON('/inspection-findings/{id}', `${API_BASE}/inspection-findings/${id}`)
}

export function getInspectionJob(id: string): Promise<InspectionJob> {
  return readJSON('/inspection-jobs/{id}', `${API_BASE}/inspection-jobs/${id}`)
}

export function continueInspectionRepair(taskId: string) {
  return call('/development-tasks/{taskId}/inspection-repair', () =>
    api.POST('/development-tasks/{taskId}/inspection-repair', {
      params: { path: { taskId } },
    }),
  )
}

export function useInspectionFindings(filter: InspectionFindingsFilter = {}) {
  return useQuery({
    queryKey: inspectionFindingsQueryKey(filter),
    queryFn: () => listInspectionFindings(filter),
  })
}

export function useContinueInspectionRepair() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: continueInspectionRepair,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['inspection-findings'] })
    },
  })
}
